import React, { useState, useEffect } from 'react';
import { CampaignAnalytics, Campaign } from '../../progress/types';
import { CampaignService } from '../../progress/services/CampaignService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import '../styles/CampaignResults.css';

interface FilterOptions {
  squadId: string;
  dateRange: {
    start: string;
    end: string;
  };
  campaignName: string;
}

const CampaignResults: React.FC = () => {
  const [analytics, setAnalytics] = useState<CampaignAnalytics[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [squads, setSquads] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    squadId: '',
    dateRange: {
      start: '',
      end: ''
    },
    campaignName: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load campaigns and squads in parallel
      const [campaignsList, squadsSnapshot] = await Promise.all([
        CampaignService.getAllCampaigns(),
        getDocs(collection(db, 'squads'))
      ]);

      setCampaigns(campaignsList);
      setSquads(squadsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || `Squad ${doc.id}`
      })));

      // Load analytics with no filters initially
      await loadAnalytics();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const dateRange = filters.dateRange.start && filters.dateRange.end ? {
        start: new Date(filters.dateRange.start),
        end: new Date(filters.dateRange.end)
      } : undefined;

      const campaignId = filters.campaignName ? 
        campaigns.find(c => c.name === filters.campaignName)?.id : 
        undefined;

      const analyticsData = await CampaignService.getCampaignAnalytics(
        campaignId,
        filters.squadId || undefined,
        dateRange
      );

      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string | { start: string; end: string }) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      squadId: '',
      dateRange: { start: '', end: '' },
      campaignName: ''
    });
  };

  const formatPercentage = (value: number, total: number): string => {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  };

  const getTopAnswer = (distribution: Record<string, number>): { answer: string; count: number; percentage: string } => {
    const entries = Object.entries(distribution);
    if (entries.length === 0) return { answer: 'No responses', count: 0, percentage: '0%' };
    
    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    const [topAnswer, topCount] = entries.reduce((max, current) => 
      current[1] > max[1] ? current : max
    );
    
    return {
      answer: topAnswer,
      count: topCount,
      percentage: formatPercentage(topCount, total)
    };
  };

  return (
    <div className="campaign-results">
      <div className="campaign-results__header">
        <h2 className="campaign-results__title">Campaign Results & Analytics</h2>

        {/* Filters */}
        <div className="campaign-results__filters">
          <h3 className="campaign-results__filters-title">Filters</h3>
          
          <div className="campaign-results__filters-row">
            {/* Squad Filter */}
            <div className="campaign-results__filter-group">
              <label className="campaign-results__filter-label">
                Squad
              </label>
              <select
                value={filters.squadId}
                onChange={(e) => handleFilterChange('squadId', e.target.value)}
                className="campaign-results__filter-select"
              >
                <option value="">All Squads</option>
                {squads.map(squad => (
                  <option key={squad.id} value={squad.id}>{squad.name}</option>
                ))}
              </select>
            </div>

            {/* Campaign Filter */}
            <div className="campaign-results__filter-group">
              <label className="campaign-results__filter-label">
                Campaign
              </label>
              <select
                value={filters.campaignName}
                onChange={(e) => handleFilterChange('campaignName', e.target.value)}
                className="campaign-results__filter-select"
              >
                <option value="">All Campaigns</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.name}>{campaign.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Range */}
          <div className="campaign-results__filters-row">
            <div className="campaign-results__filter-group">
              <label className="campaign-results__filter-label">
                Start Date
              </label>
              <input
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
                className="campaign-results__filter-input"
              />
            </div>

            <div className="campaign-results__filter-group">
              <label className="campaign-results__filter-label">
                End Date
              </label>
              <input
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
                className="campaign-results__filter-input"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="campaign-results__filter-actions">
            <button
              onClick={loadAnalytics}
              disabled={loading}
              className={`campaign-results__button ${
                loading
                  ? 'campaign-results__button--loading'
                  : 'campaign-results__button--primary'
              }`}
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>

            <button
              onClick={clearFilters}
              className="campaign-results__button campaign-results__button--secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="campaign-results__loading">
          <div className="campaign-results__loading-icon">📊</div>
          <p className="campaign-results__loading-text">Loading analytics...</p>
        </div>
      ) : analytics.length === 0 ? (
        <div className="campaign-results__empty">
          <div className="campaign-results__empty-icon">📈</div>
          <h3 className="campaign-results__empty-title">No Data Found</h3>
          <p className="campaign-results__empty-text">
            No campaign responses found for the selected filters. Try adjusting your filters or check back later.
          </p>
        </div>
      ) : (
        <div className="campaign-results__analytics">
          {analytics.map((campaignData) => (
            <div key={campaignData.campaignId} className="campaign-results__campaign-card">
              <h3 className="campaign-results__campaign-title">
                {campaignData.campaignName}
              </h3>

              {/* Summary Stats */}
              <div className="campaign-results__campaign-header">
                <div className="campaign-results__metric">
                  <span className="campaign-results__metric-value">
                    {campaignData.totalExposures}
                  </span>
                  <span className="campaign-results__metric-label">Total Exposures</span>
                </div>

                <div className="campaign-results__metric">
                  <span className="campaign-results__metric-value">
                    {campaignData.totalAnswers}
                  </span>
                  <span className="campaign-results__metric-label">Total Answers</span>
                </div>

                <div className="campaign-results__metric">
                  <span className="campaign-results__metric-value">
                    {campaignData.totalExposures > 0 ? formatPercentage(campaignData.totalAnswers, campaignData.totalExposures) : '0%'}
                  </span>
                  <span className="campaign-results__metric-label">Response Rate</span>
                </div>
              </div>

              {/* Question-level Analysis */}
              <div className="campaign-results__questions">
                <h4 className="campaign-results__question-title">Question Breakdown</h4>

                {campaignData.responses.map((response, index) => (
                  <div key={response.questionId} className="campaign-results__question-card">
                    <div className="campaign-results__question-stats">
                      <div>
                        <h5 className="campaign-results__question-title">
                          Question {index + 1} ({response.type === 'multiple_choice' ? 'Multiple Choice' : 'Text Response'})
                        </h5>
                        <div className="campaign-results__response-count">
                          {response.answers.length} response{response.answers.length === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>

                    {response.type === 'multiple_choice' && Object.keys(campaignData.answerDistribution).length > 0 && (
                      <div className="campaign-results__distribution">
                        <h6 className="campaign-results__distribution-title">Answer Distribution:</h6>
                        {Object.entries(campaignData.answerDistribution)
                          .sort(([,a], [,b]) => b - a)
                          .map(([answer, count]) => (
                            <div key={answer} className="campaign-results__answer-item">
                              <span className="campaign-results__answer-text">{answer}</span>
                              <span className="campaign-results__answer-count">
                                {count} ({formatPercentage(count, response.answers.length)})
                              </span>
                            </div>
                          ))}
                      </div>
                    )}

                    {response.type === 'text' && (
                      <div className="campaign-results__distribution">
                        <h6 className="campaign-results__distribution-title">Recent Responses:</h6>
                        <div className="campaign-results__text-responses">
                          {response.answers.slice(0, 10).map((answer, answerIndex) => (
                            <div key={answerIndex} className="campaign-results__text-response">
                              <div className="campaign-results__answer-text">"{answer.answer}"</div>
                              <div className="campaign-results__response-date">
                                {answer.submittedAt.toDate().toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                          {response.answers.length > 10 && (
                            <div className="campaign-results__more-responses">
                              ... and {response.answers.length - 10} more response{response.answers.length - 10 === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignResults;